import { Link, Text } from '@chakra-ui/react';

import { NavLink as RouterLink } from 'react-router-dom';

import { Badge, BadgeStatus, IndividualDeductionsReportStatus } from '@aslanpay/portal-common-lib';

import { CellContext, createColumnHelper, HeaderContext } from '@tanstack/react-table';

import {
  IndividualDeductionsReportActions,
  IndividualDeductionsReportRow,
} from 'app/components/UI/Table/Table.types';
import TextWithClipboard from 'app/components/UI/TextWithClipboard';

import IndividualDeductionsReportsStatusValues from 'app/constants/individualDeductionsReports';
import { IndividualDeductionsReportField } from 'app/constants/table';

import { getEmployeeProfilePath, getSelectedCompanyPath } from 'app/utils/routes';
import { formatCurrency } from 'app/utils/strings';

function getIndividualDeductionsReportsColumns(
  actions: IndividualDeductionsReportActions,
  showIndividualColumn = false,
  showCompanyColumn = false,
  enableEmployeeNameSort = false
) {
  const columnHelper = createColumnHelper<IndividualDeductionsReportRow>();

  const columns = Object.keys(IndividualDeductionsReportField).map((field) => {
    return columnHelper.accessor(field as keyof IndividualDeductionsReportRow, {
      cell: ({ row, getValue }) => {
        const value = getValue() as string;

        switch (field as keyof typeof IndividualDeductionsReportField) {
          case 'id':
            return value && <TextWithClipboard value={value} />;
          case 'companyName':
            return (
              <Link
                as={RouterLink}
                to={getSelectedCompanyPath(row.original.companyId)}
                color="brand.purple"
                textDecoration="underline"
                onClick={(event) => event.stopPropagation()}
              >
                {value as string}
              </Link>
            );
          case 'employeeName':
            return (
              <Link
                as={RouterLink}
                to={getEmployeeProfilePath(row.original.individualId)}
                color="brand.purple"
                textDecoration="underline"
                onClick={(event) => event.stopPropagation()}
              >
                {value as string}
              </Link>
            );
          case 'salaryDeductions':
            return (
              <Text textAlign="right">
                {formatCurrency(value as unknown as number, row.original.currency)}
              </Text>
            );
          case 'transactionIds':
            return value ? value.length : 0;
          case 'status': {
            const statusDetails =
              IndividualDeductionsReportsStatusValues[value as IndividualDeductionsReportStatus];

            return (
              <Badge
                value={statusDetails?.value ?? '-'}
                status={(statusDetails?.color ?? 'created') as BadgeStatus}
              />
            );
          }
          default:
            return value;
        }
      },
      header:
        IndividualDeductionsReportField[field as keyof typeof IndividualDeductionsReportField],
      enableSorting: [
        'id',
        ...(enableEmployeeNameSort ? ['employeeName'] : []),
        'externalPayrollId',
        'salaryDeductions',
        'status',
      ].includes(field),
      meta: {
        getHeaderContext: (context: HeaderContext<IndividualDeductionsReportRow, unknown>) => {
          if (context.header.id === 'salaryDeductions') {
            return { style: { justifyContent: 'end' } };
          }
          return undefined;
        },
        getCellContext: (context: CellContext<IndividualDeductionsReportRow, unknown>) => {
          return {
            onClick: () => actions.select(context.row.original.id),
          };
        },
      },
    });
  });

  if (!showIndividualColumn) {
    const individualColumnIndex = columns.findIndex((column) => column.header === 'Employee');
    if (individualColumnIndex > -1) {
      columns.splice(individualColumnIndex, 1);
    }
  }

  if (!showCompanyColumn) {
    const companyColumnIndex = columns.findIndex((column) => column.header === 'Company');
    if (companyColumnIndex > -1) {
      columns.splice(companyColumnIndex, 1);
    }
  }

  return columns;
}

export default getIndividualDeductionsReportsColumns;
